import { CloudUpload, Edit } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Alert, AlertColor, Box, IconButton, Snackbar, Typography, styled } from '@mui/material';
import { format } from 'date-fns';
import React, { useEffect, useState } from 'react';
import Avatar from '../../../avatar/Avatar';
import Breadcrumbs from '../../../breadcrumbs/Breadcrumbs';
import { apiPaths, authHeader, handleResponse } from '../../../queryClient';
import { Company, UserRole } from '../../../types';
import { useDesktop } from '../../../useDesktop';
import EditDialog from './EditDialog';

export interface ProfileProps {
  avatar: string;
  company: Company;
  email?: string;
  joinedDate: Date;
  name: string;
  phone?: string;
}

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const humanizeUserRoles = {
  [UserRole.AccountManager]: 'Account Manager',
  [UserRole.AreaDeveloper]: 'Area Developer',
  [UserRole.Author]: 'Author',
  [UserRole.CourseManager]: 'Course Manager',
  [UserRole.Instructor]: 'Instructor',
  [UserRole.Member]: 'Member',
  [UserRole.Student]: 'Student',
};

const errorMessageFileSize = 'Failed to upload an image. The image maximum size is 500KB.';

const ProfileInfo = ({ avatar, company, email, joinedDate, name, phone }: ProfileProps) => {
  const isDesktop = useDesktop();
  const [uploading, setUploading] = useState(false);
  const [showPopupToast, setShowPopupToast] = useState(false);
  const [avatarSrc, setAvatarSrc] = useState<string>(avatar);

  const [toastMessage, setToastMessage] = useState<string>();
  const [toastSeverity, setToastSeverity] = useState<AlertColor>();

  const [showEditTitleDialog, setShowEditTitleDialog] = useState(false);

  useEffect(() => {
    if (avatar) {
      setAvatarSrc(avatar);
    }
  }, [avatar]);

  const onFileChange = async event => {
    const files = event.target.files;
    setUploading(true);

    if (files?.length && files[0] && Math.floor(files[0].size / 1024) > 500) {
      setToastMessage(errorMessageFileSize);
      setToastSeverity('error');
      setShowPopupToast(true);
      setUploading(false);

      event.target.value = null;

      return;
    }

    const formData = new FormData();
    formData.append('profilePicture', files[0]);

    fetch(apiPaths.uploadPicture, {
      headers: { ...(await authHeader()), enctype: 'multipart/form-data' },
      body: formData,
      method: 'POST',
    })
      .then(handleResponse)
      .then(() => {
        setToastSeverity('success');
        setToastMessage('Image uploaded successfully.');
        setShowPopupToast(true);
        setUploading(false);

        setAvatarSrc((window.URL || window.webkitURL).createObjectURL(files[0]));
      })
      .catch(async error => {
        const body = await error?.json();

        setToastSeverity('error');
        setToastMessage(
          body?.errors?.profilePicture?.includes('500')
            ? errorMessageFileSize
            : body?.errors?.profilePicture || 'Something went wrong. Please try again.',
        );
        setShowPopupToast(true);
        setUploading(false);

        event.target.value = null;
      });
  };

  return (
    <>
      <Breadcrumbs items={[{ to: '/', label: 'Home' }]} sx={{ display: { xs: 'none', sx: 'flex' } }} />
      <Box alignItems="center" display="flex" flexDirection="column" gap={{ xs: 1, sm: 2 }}>
        <Avatar
          src={avatarSrc}
          sx={{
            borderRadius: 4,
            height: { xs: 133, sm: 156 },
            width: { xs: 133, sm: 156 },
          }}
        />

        <LoadingButton
          loading={uploading}
          component="label"
          variant="contained"
          startIcon={<CloudUpload />}
          onClick={() => setShowPopupToast(false)}
          onChange={onFileChange}
        >
          Upload photo
          <VisuallyHiddenInput type="file" accept="image/*" />
        </LoadingButton>

        <div>
          <Typography component="h2" variant={isDesktop ? 'h4' : 'h5'}>
            {name}
            {phone && (
              <Typography component="span" ml={1} variant={isDesktop ? 'h4' : 'h5'}>
                ({phone})
              </Typography>
            )}
            <IconButton size="small" onClick={() => setShowEditTitleDialog(true)} sx={{ ml: 1 }}>
              <Edit />
            </IconButton>
          </Typography>

          <Typography variant={'body2'} color="text.secondary">
            Joined {format(joinedDate, 'MMMM yyy')}
          </Typography>

          {company?.roles.length && company?.roles[0] !== UserRole.Member && (
            <Typography variant={'body2'} color="text.secondary">
              {company.name}
            </Typography>
          )}

          <Typography variant={'body2'} color="text.secondary">
            {email}
          </Typography>
          {company?.roles && (
            <Typography variant={'body2'} color="text.secondary">
              Roles:{' '}
              {company?.roles
                .map(role => humanizeUserRoles[role] || role)
                .sort()
                .join(', ')}
            </Typography>
          )}
        </div>

        <EditDialog
          open={showEditTitleDialog}
          onClose={() => setShowEditTitleDialog(false)}
          name={name}
          phone={phone}
        />

        <Snackbar
          autoHideDuration={3000}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          open={showPopupToast}
          onClose={() => setShowPopupToast(false)}
        >
          <Alert onClose={() => setShowPopupToast(false)} severity={toastSeverity}>
            {toastMessage}
          </Alert>
        </Snackbar>
      </Box>
    </>
  );
};

export default ProfileInfo;
